import { MODULES } from '@/@core/utils/helpers'
import { userAuthState } from '@core/auth'
import { canNavigate } from '@layouts/plugins/casl'
import { CONCIERGE_SW_SCOPE, isPwaEnabled } from './public-routes'

export const setupGuards =  router => {
  // 👉 router.beforeEach
  // Docs: https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards
  router.beforeEach(to => {
    /*
      Bedside concierge path moves, handled HERE rather than in either page's
      setup().

      A `router.replace()` called synchronously from a route component's setup
      can be swallowed while the initial navigation is still resolving, and
      this repo has no way to prove otherwise -- the app shell does not mount
      route components locally without a resolvable tenant. Returning a
      redirect from beforeEach is the documented mechanism and runs on the
      first navigation as well as every later one, so it needs no such proof.
    */

    // MIGRATION SHIM, safe to delete once no `?t=` link is in circulation.
    // /service-desk used to serve the chat too whenever ?t= was present.
    // Nothing printed carries that shape -- the scan-to-concierge flow was
    // never launched -- but a token pasted into a demo or a bookmark would
    // otherwise land on the complaint form, which pushes to invalid-url when
    // `s` is absent.
    if (to.path === '/service-desk' && to.query.t)
      return { path: '/service-desk/chat', query: to.query, replace: true }

    // A chat URL with no bed token is malformed: a truncated scan, or a
    // hand-typed path. It cannot open anything.
    if (to.path === '/service-desk/chat' && !to.query.t)
      return { path: '/invalid-url', replace: true }

    /*
         * If it's a public route, continue navigation. This kind of pages are allowed to visited by login & non-login users. Basically, without any restrictions.
         * Examples of public routes are, 404, under maintenance, etc.
         */

    // Set page title from tenant name
    const tenantConfig = JSON.parse(localStorage.getItem('tenant_config') || 'null')
    const tenantName = tenantConfig?.name || import.meta.env.VITE_APP_NAME

    document.title = tenantName
    if (to.meta.title)
      document.title = `${to.meta.title} - ${tenantName}`
        
    // Optionally unregister service worker for public routes.
    //
    // The bedside concierge is the one exception: it registers its own
    // minimal worker (public/concierge-sw.js, scoped to /service-desk) purely
    // to receive web push about a patient's tickets. Unregistering it here
    // would silently kill patient notifications -- the registration is torn
    // down on the very navigation that opens the chat, so nothing would ever
    // arrive and there would be no error to see.
    //
    // Scope is what distinguishes them: the staff PWA registers at '/', this
    // one at '/service-desk/chat' -- narrow enough that it controls the bedside
    // chat only, not the public complaint form sharing the parent path.
    if (!isPwaEnabled(to.path) && 'serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          if (registration.scope.includes(CONCIERGE_SW_SCOPE)) return

          registration.unregister()
        })
      })
    }
    
    if (to.meta.public) return undefined

    /**
         * Check if user is logged in by checking if token & user data exists in local storage
         * Feel free to update this logic to suit your needs
         */

    const auth = userAuthState()
    const isLoggedIn = auth.isLoggedIn
    const hasTempToken = auth.tempToken

    /*
          If user is logged in and is trying to access login like page, redirect to home
          else allow visiting the page
          (WARN: Don't allow executing further by return statement because next code will check for permissions)
         */

    // Allow access to two-step verification if there's a temp token
    if (to.name === 'app-two-step' && hasTempToken) {
      return undefined
    }

    // Redirect to two-step if trying to access protected routes with only temp token
    if (hasTempToken && !isLoggedIn && to.name !== 'app-two-step' && !to.meta.public && !to.meta.unauthenticatedOnly) {
      return { name: 'app-two-step' }
    }

    if (to.meta.unauthenticatedOnly) return undefined

    /*
          Deny by default: everything that is not explicitly marked `public` or `unauthenticatedOnly`
          needs a session. Without this, any route that omits `meta.subject`/`meta.action`
          (e.g. app-panel) would fall through the permission check below and render for anonymous users.
         */
    if (!isLoggedIn) {
      return {
        name: 'app-login',
        query: { to: to.fullPath },
      }
    }

    if (isLoggedIn) {
      // If user is logged in, redirect from unauthenticatedOnly pages
      if (to.name === 'app-login' || to.name === 'app-forgot-password' || to.name === 'app-reset-password' || to.name === 'app-two-step') {
        const modules = auth.user?.modules || []

        if (to.query.to && to.query.to?.split('/')?.[2] && modules.find(x => x.name.toLowerCase() === to.query.to.split('/')[2])) {
          const module = MODULES.find(x => x.name.toLowerCase() === to.query.to.split('/')[2])
          if (module)
            return module.to
        }
        if (modules.length === 1) {
          const module = MODULES.find(x => x.name === modules[0].name)
          if (module)
            return module.to
        }
        
        return { name: 'app-panel' }
      }

      // Handle single module redirection from panel
      if (to.name === 'app-panel') {
        const modules = auth.user?.modules || []
        if (modules.length === 1) {
          const module = MODULES.find(x => x.name === modules[0].name)
          if (module)
            return module.to
        } else if (modules.length > 1) {
          return // Go To Panel Page
        }
      }
    }


    // Anonymous visitors already returned above, so this only runs for a signed-in user
    // who is missing the ability the route asks for.
    const requiresPermission = to.matched.some(route => route.meta.subject && route.meta.action)
    if (requiresPermission && !canNavigate(to))
      return { name: 'app-not-authorized' }
    
  })
}
